// @flow

import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorAlert } from '../../ErrorAlert'
import { PlaybackErrorModal } from '../../PlaybackErrorModal'
import {
  Container,
  CentralContainer,
  ErrorAlertContainer,
  PlaybackLoader,
} from './BaseControls.style'
import { usePlayerContext, RECOVERY_PHASES } from '../../../store'

type BaseControlsInnerProps = {
  className?: string,
  isLoading?: boolean,
  errorMessage?: ?string,
  errorCode?: ?(string | number),
  isFailed?: boolean,
  onRetry?: () => void,
  onDismiss?: () => void,
}

// eslint-disable-next-line complexity
const BaseControlsInnerPure = ({
  className,
  isLoading,
  errorMessage,
  errorCode,
  isFailed,
  onRetry,
  onDismiss,
}: BaseControlsInnerProps) => {
  const { t } = useTranslation()

  // The modal offers a retry; once closed, the error stays as a notice
  if (isFailed && onRetry && onDismiss) {
    return (
      <Container className={className}>
        <PlaybackErrorModal
          message={errorMessage}
          code={errorCode}
          onRetry={onRetry}
          onClose={onDismiss}
        />
      </Container>
    )
  }

  return (
    <Container className={className}>
      {isLoading && (
        <CentralContainer>
          <PlaybackLoader />
        </CentralContainer>
      )}

      {errorMessage && (
        <ErrorAlertContainer>
          <ErrorAlert title={t('mwstv_tv_playback_error')} description={errorMessage} />
        </ErrorAlertContainer>
      )}
    </Container>
  )
}

const BaseControlsInner = memo(BaseControlsInnerPure)

type BaseControlsProps = {
  className?: string,
  onRetry?: () => void,
  onDismiss?: () => void,
}

const BaseControlsPure = ({ className, onRetry, onDismiss }: BaseControlsProps) => {
  const [state] = usePlayerContext()

  return (
    <BaseControlsInner
      className={className}
      // After the modal is closed the stopped engine shows neither the loader nor the error
      isLoading={state.loading && state.recovery !== RECOVERY_PHASES.DISMISSED}
      errorMessage={state.error?.message}
      errorCode={state.error?.code}
      isFailed={state.recovery === RECOVERY_PHASES.FAILED}
      onRetry={onRetry}
      onDismiss={onDismiss}
    />
  )
}

export const BaseControls = memo(BaseControlsPure)

export default BaseControls
